import { useCallback } from "react";

import { LIMIT_DAYS_LIST, LIMIT_TIMES_LIST } from "@/components/features/items/edit/_shared/constants/reserve-limit";

import { convertRequiredPrepMillisecondBeforeDateAndHourIntoRequiredPrepMillisecond } from "../../../../../../../../../_shared/validation-schema/converter";

import type { ForNodeType } from "../../../../../../../../../_model/types/auto-generator-for-view-model";

type Props = {
	requiredPrepMillisecond: ForNodeType<number>;
	requiredPrepMillisecondBeforeHour: ForNodeType<number>;
	requiredPrepMillisecondBeforeDate: ForNodeType<number>;

	onSet: <
		K extends "requiredPrepMillisecondBeforeHour" | "requiredPrepMillisecondBeforeDate" | "requiredPrepMillisecond",
	>(
		values: { key: K; value: ForNodeType<number> }[],
	) => void;
};

export const useRequiredPrepMillisecond = ({
	requiredPrepMillisecond: _requiredPrepMillisecond,
	requiredPrepMillisecondBeforeDate,
	requiredPrepMillisecondBeforeHour,
	onSet,
}: Props) => {
	const isEdited = requiredPrepMillisecondBeforeDate.isEdited || requiredPrepMillisecondBeforeHour.isEdited;
	const hasError = Boolean(
		requiredPrepMillisecondBeforeDate.error || requiredPrepMillisecondBeforeHour.error || false,
	);

	const requiredPrepMillisecondBeforeHourOptions = LIMIT_TIMES_LIST.map((item, idx) => {
		return {
			value: `${item.value}`,
			label: `${item.key}`,
			id: `requiredPrepMillisecond_hour_${idx}`,
		};
	});

	const onSetRequiredPrepMillisecondBeforeHour = useCallback(
		(input: string) => {
			const inputNumber = Number(input);

			const requiredPrepMillisecond = convertRequiredPrepMillisecondBeforeDateAndHourIntoRequiredPrepMillisecond({
				requiredPrepMillisecondBeforeDate: requiredPrepMillisecondBeforeDate.value,
				requiredPrepMillisecondBeforeHour: inputNumber,
			});

			onSet([
				{
					key: "requiredPrepMillisecondBeforeHour",
					value: {
						...requiredPrepMillisecondBeforeHour,
						value: inputNumber,
					},
				},
				{
					key: "requiredPrepMillisecond",
					value: {
						..._requiredPrepMillisecond,
						value: requiredPrepMillisecond,
					},
				},
			]);
		},
		[_requiredPrepMillisecond, onSet, requiredPrepMillisecondBeforeDate.value, requiredPrepMillisecondBeforeHour],
	);

	const requiredPrepMillisecondBeforeDateOptions = LIMIT_DAYS_LIST.map((item, idx) => {
		return {
			value: `${item.value}`,
			label: `${item.key}`,
			id: `requiredPrepMillisecond_date_${idx}`,
		};
	});

	const onSetRequiredPrepMillisecondBeforeDate = useCallback(
		(input: string) => {
			const inputNumber = Number(input);

			const requiredPrepMillisecond = convertRequiredPrepMillisecondBeforeDateAndHourIntoRequiredPrepMillisecond({
				requiredPrepMillisecondBeforeDate: inputNumber,
				requiredPrepMillisecondBeforeHour: requiredPrepMillisecondBeforeHour.value,
			});

			onSet([
				{
					key: "requiredPrepMillisecondBeforeDate",
					value: {
						isEdited: true,
						value: inputNumber,
					},
				},
				{
					key: "requiredPrepMillisecond",
					value: {
						..._requiredPrepMillisecond,
						value: requiredPrepMillisecond,
					},
				},
			]);
		},
		[_requiredPrepMillisecond, onSet, requiredPrepMillisecondBeforeHour.value],
	);

	return {
		isEdited,
		hasError,

		requiredPrepMillisecondBeforeDate,
		onSetRequiredPrepMillisecondBeforeDate,
		requiredPrepMillisecondBeforeDateOptions,

		requiredPrepMillisecondBeforeHour,
		onSetRequiredPrepMillisecondBeforeHour,
		requiredPrepMillisecondBeforeHourOptions,
	};
};
